<script setup lang="ts">
/**
 * „Kachel“: one thing of a small set shown as a card in an `NxTileGrid` — a
 * profile, a workspace, a connected account. The head holds a media slot (an
 * `NxAvatar`, an icon), the title (`title` or the `#title` slot, e.g. an
 * `NxInlineEdit` to rename in place), a `#meta` line under it (markers such as
 * „★ Standard“) and a `#menu` in the corner (an `NxMenuButton` with the
 * secondary actions). The default slot is the body, the `#footer` the one
 * primary action — or, on the current tile, its state.
 *
 * `current` highlights the one tile that is in use („Aktiv“): brand border and
 * tint, `aria-current`. The tile itself is no control; its buttons are.
 * `loading` draws a skeleton of the same shape while the data loads.
 *
 * A launch tile — an app on a start page, a tip, a shortcut — sets
 * `activatable`: the title becomes the one native button of the tile, stretched
 * over its whole surface, and `activate` fires on click, Enter and Space. The
 * `description` (two lines at most when stacked) is read as its description. `disabled`
 * keeps the tile visible but inert and shows `disabledReason` as its tooltip.
 * Controls of their own go into `#menu` and `#footer`, which stay clickable on
 * top of the button. `icon` draws the brand icon square (the `#media` slot
 * replaces it); `layout="stacked"` puts the media above the text — in an
 * activatable tile it lifts and blooms on hover — and `compact` is the denser
 * tile with the small title.
 */
import { computed, inject, useId, type Component } from 'vue';
import NxSkeleton from '../Loading/NxSkeleton.vue';
import {
  countBadgeName,
  hasCountBadge,
  type NxCountBadgeTone,
  type NxCountBadgeValue,
} from '../lib/countBadge';
import NxCountBadge from '../lib/NxCountBadge.vue';
import { tileGridKey } from './context';

const props = withDefaults(
  defineProps<{
    title?: string;
    /** The tile in use (the active profile): highlighted, `aria-current`. */
    current?: boolean;
    /** A skeleton instead of the content, hidden from assistive technology. */
    loading?: boolean;
    /** The title is the tile's one button, stretched over the whole tile. */
    activatable?: boolean;
    /** Visible but inert; with `activatable` the button is disabled. */
    disabled?: boolean;
    /** Tooltip of a `disabled` tile: why it cannot be used. */
    disabledReason?: string;
    /** Text under the title; stacked, clamped to two lines (the room is kept). */
    description?: string;
    /** Icon component for the brand icon square left of / above the title. */
    icon?: Component;
    /** `stacked` puts the media above the text; `inline` (default) beside it. */
    layout?: 'inline' | 'stacked';
    /** Denser tile: small title, even padding. */
    compact?: boolean;
    /**
     * Count sticker on the media's corner: unread messages, open items. `0`,
     * `''`, `null` and `undefined` show nothing, a tile without media neither.
     * An activatable tile says it in its button's name.
     */
    badge?: NxCountBadgeValue;
    /** Colour of the sticker: `brand` (default), `neutral`, `warning` or `danger`. */
    badgeTone?: NxCountBadgeTone;
    /** Spoken meaning of the sticker („3 ungelesen“); replaces the bare count in the name. */
    badgeLabel?: string;
    /** The sticker as a dot, without the count. */
    badgeDot?: boolean;
  }>(),
  {
    title: undefined,
    current: false,
    loading: false,
    activatable: false,
    disabled: false,
    disabledReason: undefined,
    description: undefined,
    icon: undefined,
    layout: 'inline',
    compact: false,
    badge: undefined,
    badgeTone: 'brand',
    badgeLabel: undefined,
    badgeDot: false,
  }
);

const emit = defineEmits<{
  /** Click, Enter or Space on an `activatable` tile. */
  activate: [];
}>();

defineSlots<{
  /** Left of the title: an avatar or an icon. */
  media?(): unknown;
  /** Replaces the `title` text, e.g. with an inline rename. */
  title?(): unknown;
  /** Under the title: markers, a short description. */
  meta?(): unknown;
  /** Top right corner: the overflow menu. */
  menu?(): unknown;
  /** The body under the head. */
  default?(): unknown;
  /** The bottom row: the primary action, or the tile's state. */
  footer?(): unknown;
}>();

const inGrid = inject(tileGridKey, false);
const descriptionId = useId();
// Only with a badge: without one, the button is named by its text as ever.
const actionName = computed(() =>
  hasCountBadge(props.badge) && props.title
    ? countBadgeName(props.title, props.badgeDot ? 1 : props.badge, props.badgeLabel)
    : undefined
);
</script>

<template>
  <div
    class="nx-tile"
    :class="{
      'is-current': props.current,
      'is-loading': props.loading,
      'is-activatable': props.activatable,
      'is-disabled': props.disabled,
      'is-stacked': props.layout === 'stacked',
      'is-compact': props.compact,
    }"
    :title="props.disabled && !props.loading ? props.disabledReason : undefined"
    :role="inGrid ? 'listitem' : undefined"
    :aria-current="props.current && !props.loading ? 'true' : undefined"
    :aria-hidden="props.loading || undefined"
    data-testid="nx-tile"
  >
    <template v-if="props.loading">
      <div class="nx-tile__head">
        <NxSkeleton class="nx-tile__media" variant="circle" height="var(--nxt-control-size-lg)" />
        <div class="nx-tile__text">
          <NxSkeleton variant="line" width="60%" />
          <NxSkeleton variant="line" width="35%" />
        </div>
      </div>
      <div class="nx-tile__footer">
        <NxSkeleton variant="rect" width="40%" height="var(--nxt-control-size-sm)" />
      </div>
    </template>
    <template v-else>
      <div class="nx-tile__head">
        <div v-if="$slots.media || props.icon" class="nx-tile__media">
          <slot name="media">
            <span class="nx-tile__icon" aria-hidden="true">
              <component :is="props.icon" :stroke-width="2.25" class="nxt-icon-md" />
            </span>
          </slot>
          <NxCountBadge
            v-if="hasCountBadge(props.badge)"
            :badge="props.badge"
            :tone="props.badgeTone"
            :dot="props.badgeDot"
          />
        </div>
        <div class="nx-tile__text">
          <div class="nx-tile__title">
            <slot name="title">
              <button
                v-if="props.activatable"
                type="button"
                class="nx-tile__action"
                :disabled="props.disabled"
                :aria-label="actionName"
                :aria-describedby="props.description ? descriptionId : undefined"
                @click="emit('activate')"
              >
                <span class="nx-tile__title-text">{{ props.title }}</span>
              </button>
              <span v-else class="nx-tile__title-text">{{ props.title }}</span>
            </slot>
          </div>
          <div v-if="props.description" :id="descriptionId" class="nx-tile__description">
            {{ props.description }}
          </div>
          <div v-if="$slots.meta" class="nx-tile__meta">
            <slot name="meta" />
          </div>
        </div>
        <div v-if="$slots.menu" class="nx-tile__menu">
          <slot name="menu" />
        </div>
      </div>
      <div v-if="$slots.default" class="nx-tile__body">
        <slot />
      </div>
      <div v-if="$slots.footer" class="nx-tile__footer">
        <slot name="footer" />
      </div>
    </template>
  </div>
</template>

<style scoped>
.nx-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
  padding: var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-md);
  color: var(--nxt-fg);
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-tile:hover:not(.is-loading, .is-current, .is-disabled) {
  border-color: var(--nxt-border-strong);
}
.nx-tile.is-current {
  border-color: var(--nxt-brand-border);
  /* A brand tint of the raised surface: lighter in light mode, never darker
     than the other tiles in dark mode. */
  background: color-mix(in srgb, var(--nxt-brand-bg) 7%, var(--nxt-surface-raised-bg));
  box-shadow: 0 0 0 var(--nxt-hairline) var(--nxt-brand-border);
}

.nx-tile__head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-tile__media {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
}
.nx-tile__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
/* No overflow clipping here: an inline rename shows its error below the field. */
.nx-tile__title {
  display: flex;
  min-width: 0;
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-fg);
}
.nx-tile__title-text {
  /* In the button of an activatable tile it would stay inline and never truncate. */
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-tile__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
  min-width: 0;
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-fg-muted);
}
.nx-tile__menu {
  display: flex;
  flex: none;
  align-self: flex-start;
  margin-block-start: calc(var(--nxt-space-2xs) * -1);
}
.nx-tile__body {
  min-width: 0;
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-muted);
}
/* The footer sits at the bottom, so the actions of a row of tiles line up. */
.nx-tile__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-height: var(--nxt-control-size-sm);
  margin-block-start: auto;
}

/* Compact: the small title and even padding. */
.nx-tile.is-compact {
  padding: var(--nxt-space-sm) var(--nxt-space-md);
}
.nx-tile.is-compact .nx-tile__head {
  align-items: flex-start;
}
.nx-tile.is-compact:not(.is-stacked) .nx-tile__head {
  gap: var(--nxt-space-xs);
}
.nx-tile.is-compact .nx-tile__title {
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
.nx-tile__description {
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-subtle);
}
/* The brand icon square. */
.nx-tile__icon {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--nxt-tile-size-md);
  height: var(--nxt-tile-size-md);
  border-radius: var(--nxt-radius-md);
  background: linear-gradient(
    135deg,
    var(--nxt-brand-bg) 0%,
    hsl(from var(--nxt-brand-bg) h s calc(l + 15)) 100%
  );
  color: var(--nxt-brand-fg);
  box-shadow: var(--nxt-shadow-md);
}

/* Stacked: media above the text, the description keeps its two lines. */
.nx-tile.is-stacked {
  padding: var(--nxt-space-md);
  min-height: calc(var(--nxt-size-unit) * 8.125);
}
.nx-tile.is-stacked .nx-tile__head {
  flex-direction: column;
  align-items: flex-start;
}
.nx-tile.is-stacked .nx-tile__text {
  flex: none;
  width: 100%;
}
.nx-tile.is-stacked .nx-tile__description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(var(--nxt-line-height-normal) * 2em);
}
.nx-tile.is-stacked .nx-tile__menu {
  position: absolute;
  inset-block-start: var(--nxt-space-xs);
  inset-inline-end: var(--nxt-space-xs);
  margin: 0;
}

/* Activatable: the title's button covers the whole tile. */
.nx-tile.is-activatable {
  cursor: pointer;
}
.nx-tile.is-activatable:hover:not(.is-disabled, .is-loading, .is-current) {
  background: var(--nxt-surface-sunken-bg);
}
.nx-tile__action {
  display: block;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: inherit;
}
.nx-tile__action::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--nxt-radius-lg);
}
.nx-tile__action:focus-visible {
  outline: none;
}
.nx-tile__action:focus-visible::after {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}
/* Controls of their own stay above the stretched button. */
.nx-tile.is-activatable .nx-tile__menu,
.nx-tile.is-activatable .nx-tile__footer {
  position: relative;
}
.nx-tile.is-activatable.is-stacked .nx-tile__menu {
  position: absolute;
}
.nx-tile.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* A stacked launch tile lifts and blooms a brand halo from its top-right corner. */
.nx-tile.is-activatable.is-stacked {
  isolation: isolate;
  overflow: hidden;
  transition:
    transform var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-tile.is-activatable.is-stacked::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: -40% -40% auto auto;
  width: var(--nxt-area-size-lg);
  height: var(--nxt-area-size-lg);
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--nxt-brand-bg) 16%, transparent),
    transparent 70%
  );
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-tile.is-activatable.is-stacked:hover:not(.is-disabled, .is-loading) {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-surface-raised-bg);
  transform: translateY(calc(var(--nxt-size-unit) * -0.125));
  box-shadow: var(--nxt-shadow-lg);
}
.nx-tile.is-activatable.is-stacked:hover:not(.is-disabled, .is-loading)::before {
  opacity: 1;
}
.nx-tile.is-activatable.is-stacked:active:not(.is-disabled, .is-loading) {
  transform: translateY(calc(var(--nxt-size-unit) * -0.0625));
}

@media (prefers-reduced-motion: reduce) {
  .nx-tile,
  .nx-tile.is-activatable.is-stacked,
  .nx-tile.is-activatable.is-stacked::before {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-tile.is-current {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
